<script setup lang="ts">
import { computed, useId } from "vue";
import { just, nothing } from "../lib/maybe";
import { clampMapDimension, MAP_PAGE_PRESETS } from "../lib/mapPage";
import ZwField from "./ui/ZwField.vue";
import ZwSelect from "./ui/ZwSelect.vue";

const width = defineModel<number>("width", { required: true });
const height = defineModel<number>("height", { required: true });
const widthId = useId();
const heightId = useId();
const preset = computed({
  get: (): string =>
    MAP_PAGE_PRESETS.find(
      (option) => option.value === `${width.value}x${height.value}`,
    )?.value ?? "custom",
  set: (value: string): void => {
    const [nextWidth, nextHeight] = value.split("x").map(Number);
    if (
      typeof nextWidth === "number" &&
      typeof nextHeight === "number" &&
      Number.isFinite(nextWidth) &&
      Number.isFinite(nextHeight)
    ) {
      width.value = nextWidth;
      height.value = nextHeight;
    }
  },
});
</script>

<template>
  <div class="map-size-fields">
    <ZwSelect
      :id="nothing"
      v-model="preset"
      :label="just('Map size')"
      :options="just(MAP_PAGE_PRESETS)"
    />
    <div class="map-dimensions">
      <ZwField label="Width" :for-id="just(widthId)">
        <div class="zw-input-shell map-dimension-control">
          <input
            :id="widthId"
            v-model.number="width"
            class="zw-input"
            type="number"
            inputmode="numeric"
            min="64"
            max="8192"
            step="1"
            aria-label="Width (px)"
            writingsuggestions="false"
            @blur="width = clampMapDimension(width)"
          />
          <span class="map-dimension-unit" aria-hidden="true">px</span>
        </div>
      </ZwField>
      <span class="map-dimension-divider" aria-hidden="true">×</span>
      <ZwField label="Height" :for-id="just(heightId)">
        <div class="zw-input-shell map-dimension-control">
          <input
            :id="heightId"
            v-model.number="height"
            class="zw-input"
            type="number"
            inputmode="numeric"
            min="64"
            max="8192"
            step="1"
            aria-label="Height (px)"
            writingsuggestions="false"
            @blur="height = clampMapDimension(height)"
          />
          <span class="map-dimension-unit" aria-hidden="true">px</span>
        </div>
      </ZwField>
    </div>
  </div>
</template>

<style scoped>
.map-size-fields {
  display: grid;
  gap: 0.75rem;
  min-width: 0;
}
.map-dimensions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: end;
  gap: 0.5rem;
}
.map-dimensions :deep(.zw-field) {
  min-width: 0;
}
.map-dimension-control {
  grid-template-columns: minmax(0, 1fr) auto;
}
.map-dimension-control input {
  appearance: textfield;
  height: 100%;
  font-variant-numeric: tabular-nums;
}
.map-dimension-control input::-webkit-inner-spin-button,
.map-dimension-control input::-webkit-outer-spin-button {
  appearance: none;
  margin: 0;
}
.map-dimension-unit {
  padding-right: 0.75rem;
  color: var(--text-muted);
  font-size: 0.75rem;
  pointer-events: none;
}
.map-dimension-divider {
  display: grid;
  place-items: center;
  height: 2.35rem;
  color: var(--text-muted);
  font-size: 0.875rem;
}
@media (pointer: coarse) {
  .map-dimension-control {
    min-height: 2.75rem;
  }
  .map-dimension-divider {
    height: 2.75rem;
  }
}
</style>
